import type { Sprint, Task } from '@nexo/types';
import { Avatar, Badge, Button, EmptyState, Progress, cn } from '@nexo/ui';
import { CalendarClock, Flag, Pencil, Plus, Target, Timer } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useTeam } from '../../layouts/TeamContext';
import { can } from '../../lib/permissions';
import { CompleteSprintDialog, SprintFormDialog } from './SprintDialogs';
import { BurndownChart } from './SprintCharts';
import { SprintTasks } from './SprintTasks';
import { burndown, daysLeft, pointsOf, sprintDates, workload } from './sprintUtils';

/** The «Sprint activo» tab: how the sprint is going, who carries what, and its board. */
export function ActiveSprint({
  sprint,
  tasks,
  planned,
  onPlan,
}: {
  sprint: Sprint | undefined;
  tasks: Task[];
  planned: Sprint[];
  onPlan: () => void;
}) {
  const { t } = useTranslation();
  const { team, openTask } = useTeam();
  const [dialog, setDialog] = useState<'edit' | 'complete' | null>(null);
  const manage = can(team, 'manage_tasks');

  if (!sprint) {
    return (
      <EmptyState
        icon={<Timer className="size-8" />}
        title={t('No hay ningún sprint en marcha')}
        description={t(
          'Planifica un sprint con tareas del backlog e inícialo para seguir aquí su progreso.',
        )}
        action={
          <Button icon={<CalendarClock className="size-4" />} onClick={onPlan}>
            {t('Ir a planificación')}
          </Button>
        }
      />
    );
  }

  const list = tasks.filter((task) => task.sprintId === sprint.id);
  const done = list.filter((task) => task.status === 'done');
  const doing = list.filter((task) => task.status === 'in_progress');
  const total = pointsOf(list);
  const completed = pointsOf(done);
  const left = daysLeft(sprint);
  const unestimated = list.filter(
    (task) => task.storyPoints === null && task.status !== 'done',
  ).length;
  const scopeChange =
    sprint.committed && sprint.committed.points !== total ? total - sprint.committed.points : 0;

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-4 px-3 py-4 sm:px-6">
      <header className="rounded-2xl border border-border bg-surface p-5 shadow-panel">
        <div className="flex flex-wrap items-start gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="truncate text-lg font-semibold tracking-tight">{sprint.name}</h2>
              <Badge tone="accent">{t('Activo')}</Badge>
              {left !== null && (
                <Badge tone={left < 0 ? 'danger' : left <= 2 ? 'warning' : 'neutral'}>
                  {left < 0
                    ? left === -1
                      ? t('Terminó hace 1 día')
                      : t('Terminó hace {{count}} días', { count: -left })
                    : left === 0
                      ? t('Último día')
                      : left === 1
                        ? t('Queda 1 día')
                        : t('Quedan {{count}} días', { count: left })}
                </Badge>
              )}
            </div>
            <p className="mt-0.5 text-sm text-muted">{sprintDates(sprint)}</p>
            {sprint.goal && (
              <p className="mt-3 flex items-start gap-2 text-sm">
                <Target className="mt-0.5 size-4 shrink-0 text-accent" />
                <span>{sprint.goal}</span>
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              icon={<Plus className="size-4" />}
              onClick={() => openTask({ mode: 'create', sprintId: sprint.id })}
            >
              {t('Tarea')}
            </Button>
            {manage && (
              <>
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Pencil className="size-4" />}
                  onClick={() => setDialog('edit')}
                >
                  {t('Editar')}
                </Button>
                <Button
                  size="sm"
                  icon={<Flag className="size-4" />}
                  onClick={() => setDialog('complete')}
                >
                  {t('Completar sprint')}
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label={t('Puntos completados')} value={`${completed} / ${total}`}>
          <Progress
            value={completed}
            max={total}
            label={t('Puntos completados')}
            className="mt-2"
          />
        </Stat>
        <Stat label={t('Tareas hechas')} value={`${done.length} / ${list.length}`}>
          <Progress
            value={done.length}
            max={list.length}
            label={t('Tareas hechas')}
            className="mt-2"
          />
        </Stat>
        <Stat label={t('En curso')} value={String(doing.length)}>
          <p className="mt-1 text-xs text-muted">
            {t('{{count}} sin empezar', { count: list.length - done.length - doing.length })}
          </p>
        </Stat>
        <Stat
          label={t('Alcance')}
          value={
            sprint.committed
              ? t('{{points}} pts al inicio', { points: sprint.committed.points })
              : t('{{points}} pts', { points: total })
          }
        >
          <p className={cn('mt-1 text-xs', scopeChange > 0 ? 'text-warning' : 'text-muted')}>
            {scopeChange > 0
              ? t('+{{points}} pts añadidos después', { points: scopeChange })
              : scopeChange < 0
                ? t('{{points}} pts quitados después', { points: scopeChange })
                : unestimated > 0
                  ? unestimated === 1
                    ? t('1 tarea sin estimar')
                    : t('{{count}} tareas sin estimar', { count: unestimated })
                  : t('Sin cambios desde el inicio')}
          </p>
        </Stat>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(16rem,1fr)]">
        {sprint.startDate && sprint.endDate ? (
          <BurndownChart points={burndown(sprint, list)} />
        ) : (
          <div className="rounded-2xl border border-border p-4 text-sm text-muted">
            {t('Pon fechas al sprint para ver el burndown.')}
          </div>
        )}
        <section className="rounded-2xl border border-border bg-surface p-4 shadow-panel">
          <h3 className="text-sm font-semibold">{t('Carga por persona')}</h3>
          <p className="mb-3 text-xs text-muted">{t('Puntos asignados y cuántos están hechos')}</p>
          {workload(list).length === 0 ? (
            <p className="text-sm text-muted">
              {t('Nadie tiene tareas asignadas en este sprint.')}
            </p>
          ) : (
            <ul className="flex flex-col gap-3">
              {workload(list).map(({ person, points, done: personDone, tasks: count }) => (
                <li key={person.id} className="flex items-center gap-2.5">
                  <Avatar
                    id={person.id}
                    name={person.displayName}
                    src={person.avatarUrl}
                    size="sm"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="flex items-baseline gap-2 text-sm">
                      <span className="truncate">{person.displayName}</span>
                      <span className="ml-auto shrink-0 text-xs text-muted tabular-nums">
                        {t('{{done}}/{{total}} pts', { done: personDone, total: points })} ·{' '}
                        {count === 1 ? t('1 tarea') : t('{{count}} tareas', { count })}
                      </span>
                    </p>
                    <Progress
                      value={personDone}
                      max={points}
                      label={t('Puntos hechos por {{name}}', { name: person.displayName })}
                      className="mt-1"
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <SprintTasks sprintId={sprint.id} tasks={list} />

      {dialog === 'edit' && (
        <SprintFormDialog open sprint={sprint} onClose={() => setDialog(null)} />
      )}
      {dialog === 'complete' && (
        <CompleteSprintDialog
          open
          sprint={sprint}
          tasks={list}
          planned={planned}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  );
}

function Stat({ label, value, children }: { label: string; value: string; children?: ReactNode }) {
  return (
    <div className="rounded-2xl border border-border bg-surface px-4 py-3 shadow-panel">
      <p className="text-xs text-muted">{label}</p>
      <p className="mt-0.5 text-xl font-semibold tracking-tight">{value}</p>
      {children}
    </div>
  );
}
